import { createAssetListExamples } from '@unistocks/features/dashboard/components/calculated-value/examples';
import { ValidationMessage } from '@unistocks/components/form/components/validation-message';
import { Form } from '@unistocks/components/form/form';
import { createNumberSchema, oneOf, requiredSchema } from '@unistocks/components/form/validators';
import { Column } from '@unistocks/components/layout/flex';
import { Heading } from '@unistocks/components/text/text';
import {
  AssetListLayout,
  assetListLayoutOptions
} from '@unistocks/features/dashboard/components/dashboard-data/asset-list/layout';
import { DashboardItemFrame } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { Header } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { PeriodSelect } from '@unistocks/features/dashboard/components/settings/period-select';
import { PortfolioFilter } from '@unistocks/features/dashboard/components/settings/portfolio-filter';
import { ValueListRow } from '@unistocks/features/dashboard/components/value-list-item/value-list-row';
import { ValueListTable } from '@unistocks/features/dashboard/components/value-list-table/value-list-table';
import { t } from '@unistocks/localization';
import {
  AssetListType,
  AssetType,
  DateConstant,
  DashboardItemSettingsDto,
  ValueType
} from '@unistocks/proxy/axios-client';
import { mapEnumToOptions, translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { assetTypeOptions } from '@unistocks/shared/utils/enums/asset-type-options';
import { dateConstantOptions } from '@unistocks/shared/utils/enums/date-constant';
import { addFlag, hasFlag, removeFlag } from '@unistocks/shared/utils/util';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckedState, Separator } from 'tamagui';
import { z } from 'zod';

const assetListTypeOptions = mapEnumToOptions(AssetListType, 'AssetListType');

export const assetListSchemaFactory = () =>
  z.object({
    title: z.string().nullish(),
    period: requiredSchema(
      oneOf(dateConstantOptions.map(({ value }) => value)),
      t('::Dashboard:Settings:DateRange')
    ),
    portfolios: z
      .array(z.string())
      .nullish()
      .refine(
        value => (value?.length ?? 0) >= 1,
        t('::ValidationError:Required:Selection', { 0: t('::Portfolios') })
      ),
    sections: z.array(z.string()).nullish(),
    assetTypes: z.array(oneOf(assetTypeOptions.map(({ value }) => value))).nullish(),
    listType: requiredSchema(
      oneOf([AssetListType.Winners, AssetListType.Losers, AssetListType.Both]),
      t('::Dashboard:Settings:AssetList:Type')
    ),
    layout: createNumberSchema().refine(
      value => value !== null && value >= 1,
      t('::ValidationError:Required:Selection', { 0: t('::Layout') })
    )
  });

export const AssetListSettingsContentStep: React.FC = () => {
  return (
    <>
      <Form.Input
        name={'title'}
        label={t('::DisplayName')}
        helpText={t('::Dashboard:Settings:Title:HelpText')}
      />
      <Form.RadioGroup
        name={'listType'}
        label={t('::Dashboard:Settings:AssetList:Type')}
        options={translateEnumOptions(assetListTypeOptions)}
      />
      <PeriodSelect isValidOption={date => date < DateConstant.Max} />
      <PortfolioFilter
        assetTypeSelectOverrides={{
          invalidAssetTypes: [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan]
        }}
      />
    </>
  );
};

AssetListSettingsContentStep.displayName = 'AssetListSettings';

export const AssetListSettingsLayoutStep: React.FC = () => {
  const { getValues, setValue, formState, getFieldState, trigger } =
    useFormContext<DashboardItemSettingsDto>();
  const [layout, period, listType, titleText] = useWatch<
    DashboardItemSettingsDto,
    ['layout', 'period', 'listType', 'title']
  >({
    name: ['layout', 'period', 'listType', 'title']
  });
  const { error, invalid } = getFieldState('layout', formState);

  const range = dateConstantOptions.find(option => option.value === period)!;
  const type = assetListTypeOptions.find(option => option.value === listType)!;
  const title = titleText || `${t(type.key)} - ${t(range.key)}`;

  const onCheckedChange = (value: AssetListLayout) => (checked: CheckedState) => {
    const currentLayout = getValues('layout') || 0;
    const layout = checked ? addFlag(currentLayout, value) : removeFlag(currentLayout, value);

    setValue('layout', layout);
    if (invalid) trigger('layout');
  };

  const valueType = (() => {
    let result = 0;
    if (hasFlag(layout, AssetListLayout.CurrentPrice))
      result = addFlag(result, ValueType.TotalValue);

    if (hasFlag(layout, AssetListLayout.NominalChange))
      result = addFlag(result, ValueType.NominalChange);

    if (hasFlag(layout, AssetListLayout.PercentageChange))
      result = addFlag(result, ValueType.PercentageChange);

    return result;
  })();

  const values = createAssetListExamples(valueType);

  return (
    <Column mb={'$3'}>
      {translateEnumOptions(assetListLayoutOptions).map(({ key, value }, i, arr) => {
        return (
          <React.Fragment key={value}>
            <Form.Checkbox
              name={AssetListLayout[value] as any}
              label={key}
              onCheckedChange={onCheckedChange(value)}
              $platform-web={{ size: '$4' }}
            />
            {i < arr.length - 1 ? <Separator /> : null}
          </React.Fragment>
        );
      })}
      {invalid && error?.message ? <ValidationMessage>{error.message}</ValidationMessage> : null}

      <DashboardItemFrame mx={'$1.5'} mt={'$3'}>
        <Header>
          <Heading>{title}</Heading>
        </Header>

        <ValueListTable
          firstList={values}
          renderRow={value => <ValueListRow calculatedValue={value} valueCellWidth={'$6'} />}
        />
      </DashboardItemFrame>
    </Column>
  );
};

AssetListSettingsLayoutStep.displayName = 'AssetListLayoutStep';
